import { useCallback, useState } from "react";
import { Dialog } from "@base-ui/react/dialog";
import { Toggle } from "@base-ui/react/toggle";
import { ToggleGroup } from "@base-ui/react/toggle-group";
import type { ListingItem, ListingSection } from "../../content/industry";
import { useListingItems } from "../../content/items";
import { Photo } from "./Photo";
import { SectionHeading } from "./SectionHeading";

const all = "Todas";

/** Ficha completa de un elemento, en un diálogo de Base UI (foco atrapado, Esc y clic fuera para cerrar). */
function ListingDialog({ item, open, section, container, onClose }: {
  item: ListingItem | null; open: boolean; section: ListingSection; container: HTMLElement | null; onClose: () => void;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={open => { if (!open) onClose(); }}>
      {/* Se monta en la raíz .ix del sitio: hereda sus variables de estilo y queda por encima del encabezado. */}
      <Dialog.Portal container={container}>
        <Dialog.Backdrop className="ix-dialog-backdrop" />
        <Dialog.Popup className="ix-dialog">
          {item ? (
            <div className="ix-dialog-body">
              <Photo index={item.image} remote={item.imageUrl ? { source: item.imageUrl, alt: item.imageAlt ?? item.title } : undefined} className="ix-dialog-photo" sizes="(min-width: 768px) 640px, 100vw" />
              <div className="ix-dialog-content">
                {item.location ? <p className="ix-card-location">{item.location}</p> : null}
                <Dialog.Title render={<h3 />}>{item.title}</Dialog.Title>
                {item.price ? <p className="ix-card-price"><strong>{item.price}</strong>{item.priceNote ? <span> {item.priceNote}</span> : null}</p> : null}
                <Dialog.Description>{item.details?.description}</Dialog.Description>
                <ul className="ix-specs">{item.details?.features.map(feature => <li key={feature}>{feature}</li>)}</ul>
                <div className="ix-actions">
                  {section.cta ? <a className="ix-button ix-button--primary" href={section.cta.href} onClick={onClose}>{section.cta.label}</a> : null}
                  <Dialog.Close className="ix-button ix-button--ghost">Cerrar</Dialog.Close>
                </div>
              </div>
            </div>
          ) : null}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * Fichas con cinco composiciones (habitaciones, destinos, propiedades, proyectos y productos). Si el contenido define
 * «categories», aparece un filtro (ToggleGroup de Base UI: flechas para moverse entre opciones); si una ficha tiene
 * «details», el botón abre su ficha completa en un diálogo. Los datos son de ejemplo: no hay base de datos detrás.
 */
export function Listing({ section }: { section: ListingSection }) {
  const [filter, setFilter] = useState(all);
  const [selected, setSelected] = useState<ListingItem | null>(null);
  const [open, setOpen] = useState(false);
  const [siteRoot, setSiteRoot] = useState<HTMLElement | null>(null);
  const findSiteRoot = useCallback((section: HTMLElement | null) => setSiteRoot(section?.closest<HTMLElement>(".ix") ?? null), []);
  const allItems = useListingItems(section);
  const items = filter === all ? allItems : allItems.filter(item => item.category === filter);
  // Las fichas del servidor pueden traer categorías nuevas: se suman a las de la plantilla.
  const categories = section.categories?.length
    ? [...new Set([...section.categories, ...allItems.flatMap(item => (item.category ? [item.category] : []))])]
    : [];
  return (
    <section ref={findSiteRoot} id={section.id} className={`ix-section ix-listing ix-listing--${section.variant}`} aria-labelledby={`${section.id}-title`}>
      <div className="ix-container">
        <SectionHeading id={section.id} eyebrow={section.eyebrow} title={section.title} text={section.text} />
        {categories.length ? (
          <ToggleGroup
            className="ix-tabs ix-filter"
            aria-label={section.filterLabel ?? "Filtrar"}
            value={[filter]}
            onValueChange={value => setFilter(value[0] ?? all)}
          >
            {[all, ...categories].map(category => <Toggle key={category} value={category}>{category}</Toggle>)}
          </ToggleGroup>
        ) : null}
        <p className="ix-sr-only" aria-live="polite">{items.length} resultados</p>
        <ul className="ix-cards">
          {items.map(item => <li key={item.title} className="ix-card">
            <div className="ix-card-media"><Photo index={item.image} remote={item.imageUrl ? { source: item.imageUrl, alt: item.imageAlt ?? item.title } : undefined} sizes="(min-width: 1024px) 33vw, 100vw" />{item.badge ? <span className="ix-badge">{item.badge}</span> : null}</div>
            <div className="ix-card-body">
              {item.location ? <p className="ix-card-location">{item.location}</p> : null}
              <h3>{item.title}</h3>
              <p className="ix-card-text">{item.text}</p>
              {item.meta.length ? <ul className="ix-meta">{item.meta.map(meta => <li key={meta}>{meta}</li>)}</ul> : null}
              <div className="ix-card-footer">
                {item.price ? <p className="ix-card-price"><strong>{item.price}</strong>{item.priceNote ? <span> {item.priceNote}</span> : null}</p> : <span />}
                <span className="ix-card-actions">
                  {item.link?.href ? <a className="ix-link-button" href={item.link.href} target="_blank" rel="noopener noreferrer">{item.link.label}<span className="ix-sr-only"> de {item.title} (abre en otra pestaña)</span></a> : null}
                  {item.details ? <button type="button" className="ix-link-button" onClick={() => { setSelected(item); setOpen(true); }}>Ver ficha<span className="ix-sr-only"> de {item.title}</span></button> : null}
                </span>
              </div>
            </div>
          </li>)}
        </ul>
        {items.length === 0 ? <p className="ix-note">No hay resultados con este filtro.</p> : null}
        {section.note ? <p className="ix-note">{section.note}</p> : null}
        {section.cta ? <a className="ix-button ix-button--ghost ix-listing-cta" href={section.cta.href}>{section.cta.label}</a> : null}
      </div>
      {/* La ficha elegida se conserva al cerrar para que la animación de salida no quede vacía. */}
      <ListingDialog item={selected} open={open} section={section} container={siteRoot} onClose={() => setOpen(false)} />
    </section>
  );
}
